import { useEffect, useState } from 'react';

const STORAGE_KEY = 'geofleet_geo_country';

type IpApiResponse = {
  country_code?: string;
  error?: boolean;
};

/**
 * Detects visitor country via IP (client-side). Cached in sessionStorage for the tab.
 */
export function useGeoCountry(): { countryCode: string | null; isIndia: boolean; ready: boolean } {
  const [countryCode, setCountryCode] = useState<string | null>(() => {
    if (typeof sessionStorage === 'undefined') return null;
    try {
      return sessionStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });
  const [ready, setReady] = useState(() => {
    if (typeof sessionStorage === 'undefined') return false;
    try {
      return sessionStorage.getItem(STORAGE_KEY) !== null;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const cached = sessionStorage.getItem(STORAGE_KEY);
      if (cached !== null) {
        setCountryCode(cached);
        setReady(true);
        return;
      }
    } catch {
      /* ignore */
    }

    let cancelled = false;
    fetch('https://ipapi.co/json/', { credentials: 'omit' })
      .then((r) => r.json() as Promise<IpApiResponse>)
      .then((data) => {
        if (cancelled || data.error) return;
        const cc = data.country_code?.toUpperCase() ?? null;
        if (cc) {
          try {
            sessionStorage.setItem(STORAGE_KEY, cc);
          } catch {
            /* ignore */
          }
          setCountryCode(cc);
        }
      })
      .catch(() => {
        /* keep null — fall back to default currency in UI */
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return {
    countryCode,
    isIndia: countryCode === 'IN',
    ready,
  };
}
